import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  jsonLd?: object;
}

const DEFAULT_TITLE = 'Jornal do Licitante | Editais de Licitação Públicos | BBMNET';
const DEFAULT_DESCRIPTION =
  'Acesse todos os editais de licitação publicados no BBMNET. Pregão, Concorrência, Dispensa e mais — filtre por estado, modalidade, órgão e palavra-chave. Gratuito e sem cadastro.';

function setMeta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(data: object) {
  const id = 'seo-json-ld';
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function useSEO({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title ?? DEFAULT_TITLE;
    const fullDescription = description ?? DEFAULT_DESCRIPTION;

    document.title = fullTitle;
    setMeta('description', fullDescription);
    setMeta('og:title', ogTitle ?? fullTitle, true);
    setMeta('og:description', ogDescription ?? fullDescription, true);
    setMeta('twitter:title', ogTitle ?? fullTitle);
    setMeta('twitter:description', ogDescription ?? fullDescription);

    if (canonical) {
      setLink('canonical', canonical);
      setMeta('og:url', canonical, true);
    }

    if (jsonLd) {
      setJsonLd(jsonLd);
    }

    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, canonical, ogTitle, ogDescription, jsonLd]);
}
